import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KeyedMutator } from 'swr';

import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { selectLastUpdateProductsPlanDate, setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { queryParams } from '@shared/commonFunctions';
import { DealsButton } from '@styles/StyledButtons';
import { ActionsWrapper, LeftSide } from '@styles/ContactManager/ProductStyles';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { setIsDealStructureChanged } from '@store/reducers/dealSlice';
import { IDeal, IProduct, IProductsFetch } from '../../interfaces';
import { EditProductModal } from './EditProducts/EditProductModal';
import { PitchModal } from './PitchModal';
import { ProductsTable } from './ProductsTable';
import { ProductStatus } from './constants';
import { voidAllProduct } from './queries';

interface ProductProps {
  contactAddress?: NContact.Address;
  reloadDeal: KeyedMutator<IDeal>;
  isVoidEnable: boolean;
  dealProducts?: IProductsFetch;
  mutateDealProducts: KeyedMutator<IProductsFetch>;
  isLoading: boolean;
  deal?: IDeal;
  preventDealSave: boolean;
}

export const Products = ({
  contactAddress,
  reloadDeal,
  isVoidEnable,
  dealProducts: deals,
  mutateDealProducts,
  deal,
  isLoading = false,
  preventDealSave,
}: ProductProps) => {
  const [isPitchModalOpen, setPitchModalOpen] = useState(false);
  const [checkIsVoidEnable, setCheckIsVoidEnable] = useState(false);
  const lastUpdateProductsPlanDate = useSelector(selectLastUpdateProductsPlanDate);
  const [dealsProducts, setDealsProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [editProductPosition, setEditProductPosition] = useState(0);
  const [forceVendorRepull, setForceVendorRepull] = useState<boolean | null | undefined>(null);

  const { isAdmin } = useUserAccount();
  const params = queryParams();
  const dispatch = useDispatch();
  const dealId = params.dealId;

  useEffect(() => {
    if (deals?.dealsProducts) {
      setDealsProducts(deals.dealsProducts);
      const isContracted = deals.dealsProducts.some((item: IProduct) => item.status === ProductStatus.CONTRACTED);
      setCheckIsVoidEnable(isContracted ? (isAdmin ? false : !isVoidEnable) : true);
    } else {
      setDealsProducts([]);
    }
  }, [deals, isAdmin, isVoidEnable]);

  useEffect(() => {
    mutateDealProducts();
  }, [lastUpdateProductsPlanDate]);

  const voidAllProductsHandler = useCallback(async () => {
    setLoading(true);
    await voidAllProduct(dealId, dealsProducts);
    setLoading(false);
    dispatch(setLastCommentsRefresh(new Date().toISOString()));
    mutateDealProducts();
    dispatch(setIsDealStructureChanged({ data: { isDealStructureChanged: true }, dealId }));
  }, [dealId, dealsProducts]);

  const handleEditProductClick = useCallback(() => {
    setEditProductPosition((prevState) => prevState + 1);
  }, []);

  const handleForceVendorRepullClick = useCallback((value: boolean) => {
      setForceVendorRepull(value);
  }, []);

  return (
    <>
      <ActionsWrapper>
        <LeftSide>
          <Typography variant="h3">Products</Typography>
          <DealsButton
            sx={{ margin: '5px' }}
            onClick={handleEditProductClick}
          >
            Edit Products
          </DealsButton>
          {isAdmin && (
            <DealsButton
              sx={{ margin: '5px 5px 5px 0' }}
              onClick={() => handleForceVendorRepullClick(true)}
            >
              Fresh Pull
            </DealsButton>
          )}
          <DealsButton onClick={() => setPitchModalOpen(true)}>Pitch</DealsButton>
        </LeftSide>
        <DealsButton
          disabled={checkIsVoidEnable}
          onClick={voidAllProductsHandler}
        >
          Void All
        </DealsButton>
      </ActionsWrapper>
      <ProductsTable
        isVoidEnable={isAdmin || isVoidEnable}
        dealId={dealId}
        data={dealsProducts}
        contactAddress={contactAddress}
        pageLoading={loading}
        gridLoading={isLoading}
        mutateDealProductsSummary={mutateDealProducts}
        reloadDeal={reloadDeal}
        preventDealSave={preventDealSave}
      />
      <PitchModal
        isPitchModalOpen={isPitchModalOpen}
        setPitchModalOpen={setPitchModalOpen}
        products={dealsProducts}
        deal={deal}
      />
      <EditProductModal
        hideBackdrop
        isVoidEnable={isAdmin || isVoidEnable}
        contactAddress={contactAddress}
        editProductPosition={editProductPosition}
        setEditProductPosition={setEditProductPosition}
        reloadDeal={reloadDeal}
        preventDealSave={preventDealSave} 
        forceVendorRepull={forceVendorRepull}
        setForceVendorRepull={setForceVendorRepull}
      />
    </>
  );
};
